import React, { useState, useEffect } from 'react';
import { adminService } from '../../../services/adminService';
import Icon from '../../../components/AppIcon';

const FailedCheckoutsPanel = ({ compact = false, limit = 50, refreshTrigger }) => {
  const [checkouts, setCheckouts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedId, setExpandedId] = useState(null);

  useEffect(() => {
    loadFailedCheckouts();
  }, [refreshTrigger]);

  const loadFailedCheckouts = async () => {
    setLoading(true);
    setError(null);
    try {
      // Fetch failed checkout.session.completed and invoice.payment_failed events
      const data = await adminService?.getWebhookEvents({
        limit,
        status: 'failed'
      });

      // Filter for checkout-related failures
      const checkoutFailures = data?.filter(
        (event) =>
          event?.eventType === 'checkout.session.completed' ||
          event?.eventType === 'invoice.payment_failed' ||
          event?.eventType === 'checkout.session.expired'
      );

      setCheckouts(checkoutFailures);
    } catch (err) {
      console.error('Load failed checkouts error:', err);
      setError(err?.message || 'Failed to load failed checkouts');
    } finally {
      setLoading(false);
    }
  };

  const toggleExpand = (id) => {
    setExpandedId(expandedId === id ? null : id);
  };

  const extractCustomerInfo = (payload) => {
    return {
      email: payload?.customer_email || payload?.customer_details?.email || 'N/A',
      customerId: payload?.customer || 'N/A',
      amount: payload?.amount_total || payload?.amount_due || 0,
      currency: payload?.currency || 'usd'
    };
  };

  if (loading) {
    return (
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-6 bg-muted rounded w-1/3"></div>
          <div className="space-y-3">
            <div className="h-20 bg-muted rounded"></div>
            <div className="h-20 bg-muted rounded"></div>
            <div className="h-20 bg-muted rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-destructive/10 flex items-center justify-center">
            <Icon name="XCircle" size={20} className="text-destructive" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-foreground">Failed Checkouts</h2>
            <p className="text-sm text-muted-foreground">Incomplete payment attempts requiring attention</p>
          </div>
        </div>
        {!compact && (
          <button
            onClick={loadFailedCheckouts}
            className="p-2 hover:bg-muted rounded-lg transition-colors"
          >
            <Icon name="RefreshCw" size={16} className="text-muted-foreground" />
          </button>
        )}
      </div>

      {error && (
        <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4 mb-4 flex items-start gap-3">
          <Icon name="AlertCircle" size={20} className="text-destructive flex-shrink-0 mt-0.5" />
          <p className="text-destructive text-sm">{error}</p>
        </div>
      )}

      <div className="space-y-3">
        {checkouts?.length === 0 ? (
          <div className="text-center py-8">
            <Icon name="CheckCircle" size={48} className="text-success mx-auto mb-3" />
            <p className="text-muted-foreground">No failed checkouts found</p>
            <p className="text-sm text-muted-foreground mt-1">All payment attempts are processing successfully</p>
          </div>
        ) : (
          checkouts?.map((checkout) => {
            const customerInfo = extractCustomerInfo(checkout?.payload);
            const isExpanded = expandedId === checkout?.id;

            return (
              <div
                key={checkout?.id}
                className="border border-destructive/20 rounded-lg p-4 hover:bg-destructive/5 transition-colors"
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="font-medium text-foreground">{checkout?.eventType}</span>
                      <div className="flex items-center gap-1.5 px-2 py-1 rounded-full bg-destructive/10">
                        <Icon name="XCircle" size={12} className="text-destructive" />
                        <span className="text-xs font-medium text-destructive">Failed</span>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-3 text-sm">
                      <div>
                        <span className="text-muted-foreground">Customer: </span>
                        <span className="text-foreground font-medium">{customerInfo?.email}</span>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Amount: </span>
                        <span className="text-foreground font-medium">
                          ${(customerInfo?.amount / 100)?.toFixed(2)} {customerInfo?.currency?.toUpperCase()}
                        </span>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Event ID: </span>
                        <span className="text-foreground font-mono text-xs">{checkout?.eventId}</span>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Customer ID: </span>
                        <span className="text-foreground font-mono text-xs">{customerInfo?.customerId}</span>
                      </div>
                    </div>

                    {checkout?.errorMessage && (
                      <div className="bg-destructive/10 border border-destructive/20 rounded p-3 mb-3">
                        <p className="text-sm text-destructive font-medium mb-1">Error Message:</p>
                        <p className="text-sm text-destructive">{checkout?.errorMessage}</p>
                      </div>
                    )}

                    <div className="flex items-center gap-4 text-xs text-muted-foreground">
                      <span>{adminService?.formatRelativeTime(checkout?.createdAt)}</span>
                      <span>{adminService?.formatDate(checkout?.createdAt)}</span>
                    </div>
                  </div>

                  <button
                    onClick={() => toggleExpand(checkout?.id)}
                    className="p-2 hover:bg-muted rounded-lg transition-colors flex-shrink-0"
                  >
                    <Icon
                      name={isExpanded ? 'ChevronUp' : 'ChevronDown'}
                      size={16}
                      className="text-muted-foreground"
                    />
                  </button>
                </div>

                {isExpanded && (
                  <div className="mt-4 pt-4 border-t border-border">
                    <p className="text-sm font-medium text-foreground mb-2">Full Event Payload:</p>
                    <div className="bg-muted/50 rounded p-3 overflow-x-auto">
                      <pre className="text-xs font-mono text-foreground">
                        {JSON?.stringify(checkout?.payload, null, 2)}
                      </pre>
                    </div>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default FailedCheckoutsPanel;